import ArrowClockwiseIcon from '@duyank/icons/regular/ArrowClockwise';
import ArrowCounterClockwiseIcon from '@duyank/icons/regular/ArrowCounterClockwise';
import { useEditor } from '@lidojs/design-editor';
import {
  Button,
} from "@heroui/react";
import React, {
  ChangeEvent,
  forwardRef,
  ForwardRefRenderFunction,
  useRef,
} from 'react';
import { ExportButton } from '../components/ExportButton';
import { PreviewButton } from '../components/PreviewButton';
import { htmlToJson } from '../utils/import/htmlToJson';

interface HeaderLayoutProps {
  openPreview: () => void;
}

const HeaderLayout: ForwardRefRenderFunction<
  HTMLDivElement,
  HeaderLayoutProps
> = ({ openPreview }, ref) => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const { actions, query } = useEditor();

  const handleImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function () {
        const fileContent = reader.result as string;
        if (file.type === 'application/json') {
          const jsonObject = JSON.parse(fileContent);
          actions.setData(jsonObject);
        } else if (file.type === 'text/html') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(fileContent, 'text/html');
          const jsonResult = htmlToJson(doc);
          actions.setData([jsonResult]);
        }
      };
      reader.readAsText(file);
      e.target.value = '';
    }
  };

  return (
    <div
      ref={ref}
      className="dark"
      css={{
        background: '#1E1E2D',
        padding: '12px 32px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '@media (max-width: 900px)': {
          padding: 12,
        },
      }}
    >
      <div
        css={{
          color: '#3d8eff',
          fontSize: 36,
        }}
      >
        <div
          css={{ color: 'white', height: 42, paddingTop: 6, paddingBottom: 6 }}
        >
          <a href="/" rel="noreferrer" target="_blank">
            <img alt='logo Creative Maker' css={{ maxHeight: '100%' }} src={'./assets/logo.png'} />
          </a>
        </div>
      </div>
      <div css={{ display: 'flex', alignItems: 'center', gap: 32 }}>
        <div css={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              background: '#3a3a4c',
              width: 36,
              height: 36,
              borderRadius: '50%',
              cursor: query.history.canUndo() ? 'pointer' : undefined,
              opacity: query.history.canUndo() ? 1 : 0.5,
              ':hover': {
                background: query.history.canUndo()
                  ? 'rgba(58,58,76,0.5)'
                  : undefined,
              },
            }}
            onClick={actions.history.undo}
          >
            <ArrowCounterClockwiseIcon />
          </div>
          <div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              background: '#3a3a4c',
              width: 36,
              height: 36,
              borderRadius: '50%',
              cursor: query.history.canRedo() ? 'pointer' : undefined,
              opacity: query.history.canRedo() ? 1 : 0.5,
              ':hover': {
                background: query.history.canRedo()
                  ? 'rgba(58,58,76,0.5)'
                  : undefined,
              },
            }}
            onClick={actions.history.redo}
          >
            <ArrowClockwiseIcon />
          </div>
        </div>
        <Button
          css={{
            fontWeight: 700,
            padding: 0,
            ':hover': {
              textDecoration: 'underline',
              backgroundColor: 'inherit',
            },
          }}
          size="lg"
          variant="light"
          onClick={() => uploadRef.current?.click()}
        >
          <input
            ref={uploadRef}
            accept="application/json, text/html"
            css={{ display: 'none' }}
            type="file"
            onChange={handleImport}
          />
          Import
        </Button>
        <ExportButton/>
        <PreviewButton/>
      </div>
    </div>
  );
};

export default forwardRef(HeaderLayout);
